/*
 * Pillow Decor: bundle bar for the "Build your bundle" collection template (sections/pd-bundle-bar.liquid).
 * The theme's own collection grid and filters stay as they are; on this template the + on each product card adds
 * that pillow to the bundle. The variant comes from the theme's Size/Fill filters when exactly one value is
 * picked, otherwise the section defaults (20x20 / Standard Insert). Each pillow can be changed in the bundle
 * panel. Pillows go to the cart as separate lines through the theme's product-form.
 */
(() => {
  const STORAGE_KEY = 'pd-bundle-bar';

  const escapeHtml = (value) =>
    String(value ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

  const readStorage = (key, fallback) => {
    try {
      return JSON.parse(localStorage.getItem(key)) ?? fallback;
    } catch {
      return fallback;
    }
  };
  const writeStorage = (key, value) => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch {
      /* storage unavailable: the bundle still works for this visit */
    }
  };

  function formatMoney(cents, format) {
    const withDelimiters = (amount, decimals, thousands = ',', decimal = '.') => {
      const [whole, fraction] = (amount / 100).toFixed(decimals).split('.');
      return whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (fraction ? decimal + fraction : '');
    };
    return (format || '${{amount}}').replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals': return withDelimiters(cents, 0);
        case 'amount_with_comma_separator': return withDelimiters(cents, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return withDelimiters(cents, 0, '.', ',');
        default: return withDelimiters(cents, 2);
      }
    });
  }

  // Shopify CDN image at a given width (product.js returns protocol-relative URLs)
  const sized = (src, width) => {
    if (!src) return '';
    const url = new URL(src.startsWith('//') ? `https:${src}` : src, location.origin);
    url.searchParams.set('width', width);
    return url.toString();
  };

  const handleFromCard = (card) => {
    const link = card.querySelector('a[href*="/products/"]');
    const match = link && new URL(link.href, location.origin).pathname.match(/\/products\/([^/?#]+)/);
    return match ? decodeURIComponent(match[1]) : null;
  };

  class PdBundleBar extends HTMLElement {
    async connectedCallback() {
      const config = JSON.parse(this.querySelector('[data-pd-bundle-config]').textContent);
      this.moneyFormat = config.moneyFormat;
      this.slotCount = config.slots || 5;
      this.defaults = config.defaults || {};
      this.tiers = config.tiers.filter((t) => t.qty > 0 && t.pct > 0).sort((a, b) => a.qty - b.qty);
      this.products = new Map(); // handle -> product.js data
      this.items = readStorage(STORAGE_KEY, []); // [{ key, handle, variantId }]
      this.uid = this.items.reduce((max, i) => Math.max(max, i.key), 0);
      this.$ = (sel) => this.querySelector(sel);

      this.bindEvents();
      this.decorateCards();
      new MutationObserver(() => this.decorateCards()).observe(document.querySelector('main') || document.body, { childList: true, subtree: true });

      // Load the products already in a saved bundle, dropping anything no longer available
      await Promise.all([...new Set(this.items.map((i) => i.handle))].map((h) => this.loadProduct(h).catch(() => null)));
      this.items = this.items.filter((i) => this.variant(i)?.available);
      this.save();
      this.render();
    }

    /* ---------- data ---------- */

    async loadProduct(handle) {
      if (!this.products.has(handle)) {
        this.products.set(
          handle,
          fetch(`${window.Shopify?.routes?.root || '/'}products/${encodeURIComponent(handle)}.js`).then((r) => {
            if (!r.ok) throw new Error(`Product ${handle} not found`);
            return r.json();
          }).then((product) => {
            // product.js lists options as { name, position, values }; keep just the names
            product.options = product.options.map((o) => (typeof o === 'string' ? o : o.name));
            return product;
          })
        );
      }
      const product = await this.products.get(handle);
      this.products.set(handle, product);
      return product;
    }

    product(item) {
      const p = this.products.get(item.handle);
      return p && !(p instanceof Promise) ? p : null;
    }

    variant(item) {
      return this.product(item)?.variants.find((v) => v.id === item.variantId) || null;
    }

    // What a tap adds: the theme's Size/Fill filters when exactly one value is picked, else the defaults
    wantedOptions(product) {
      const params = new URLSearchParams(location.search);
      return Object.fromEntries(
        product.options.map((name) => {
          const picked = params.getAll(`filter.v.option.${name.toLowerCase()}`);
          return [name, picked.length === 1 ? picked[0] : this.defaults[name]];
        })
      );
    }

    // Best available variant for what the customer asked for (filters or defaults). Fill matters most (an insert
    // stays an insert, Cover Only stays last), then size: the wanted size first, then squares closest to 20x20,
    // then rectangles. So with no filters: 20x20, 17x17, then 12x20, all with Standard Insert.
    pickVariant(product, wanted) {
      const sizeIndex = product.options.findIndex((name) => /size/i.test(name));
      const fillIndex = product.options.findIndex((name) => /fill/i.test(name));
      const target = (this.defaults.Size || '20x20').match(/\d+(\.\d+)?/g)?.map(Number) || [20, 20];
      const sizeRank = (value) => {
        if (value === wanted[product.options[sizeIndex]]) return -1;
        const [w, h] = (value.match(/\d+(\.\d+)?/g) || []).map(Number);
        if (!w || !h) return 1000;
        return (w === h ? 0 : 100) + Math.abs(w - target[0]) + Math.abs(h - target[1]);
      };
      const fillRank = (value) => {
        if (value === wanted[product.options[fillIndex]]) return 0;
        if (value === this.defaults.Fill) return 1;
        return /cover only/i.test(value) ? 3 : 2;
      };
      const otherRank = (v) =>
        product.options.reduce((r, name, i) => r + (i === sizeIndex || i === fillIndex || v.options[i] === wanted[name] ? 0 : 1), 0);
      const rank = (v) => [
        fillIndex >= 0 ? fillRank(v.options[fillIndex]) : 0,
        sizeIndex >= 0 ? sizeRank(v.options[sizeIndex]) : 0,
        otherRank(v),
      ];
      const compare = (a, b) => {
        const ra = rank(a);
        const rb = rank(b);
        for (let i = 0; i < ra.length; i++) if (ra[i] !== rb[i]) return ra[i] - rb[i];
        return 0;
      };
      return product.variants.filter((v) => v.available).sort(compare)[0] || null;
    }

    money(cents) {
      return formatMoney(cents, this.moneyFormat);
    }

    names(product) {
      const [line, ...rest] = product.title.split(' - ');
      return { line: line.replace(/ Pillow$/, ''), name: rest.join(' - ') || product.title };
    }

    imageFor(item, width = 160) {
      const v = this.variant(item);
      return sized(v?.featured_image?.src || this.product(item)?.featured_image, width);
    }

    tierFor(count) {
      return [...this.tiers].reverse().find((t) => count >= t.qty) || null;
    }

    nextTier(count) {
      return this.tiers.find((t) => count < t.qty) || null;
    }

    totals() {
      const subtotal = this.items.reduce((sum, i) => sum + (this.variant(i)?.price || 0), 0);
      const tier = this.tierFor(this.items.length);
      // Shopify takes a percentage discount off each line and rounds per line, so do the same
      const lines = new Map();
      this.items.forEach((i) => lines.set(i.variantId, (lines.get(i.variantId) || 0) + (this.variant(i)?.price || 0)));
      const savings = tier ? [...lines.values()].reduce((sum, lineTotal) => sum + Math.round((lineTotal * tier.pct) / 100), 0) : 0;
      return { subtotal, tier, savings, total: subtotal - savings };
    }

    save() {
      writeStorage(STORAGE_KEY, this.items);
    }

    /* ---------- product cards ---------- */

    decorateCards() {
      const counts = new Map();
      this.items.forEach((i) => counts.set(i.handle, (counts.get(i.handle) || 0) + 1));
      document.querySelectorAll('main product-card').forEach((card) => {
        const handle = card.dataset.pdHandle || handleFromCard(card);
        const title = card.querySelector('a[aria-label]')?.getAttribute('aria-label');
        if (!handle) return;
        if (card.dataset.pdHandle !== handle) card.dataset.pdHandle = handle;
        // Re-check every time: the theme re-renders card contents (e.g. image loading), which drops our elements
        if (!card.querySelector('.pd-bb-card__plus')) {
          card.classList.add('pd-bb-card');
          const overlay = card.querySelector('.product-card__overlay') || card;
          overlay.insertAdjacentHTML(
            'beforeend',
            `<button type="button" class="pd-bb-card__plus" aria-label="Add to bundle"></button><span class="pd-bb-card__count" hidden></span>`
          );
        }
        const $plus = card.querySelector('.pd-bb-card__plus');
        const plusLabel = `Add ${title || 'pillow'} to bundle`;
        if ($plus && $plus.getAttribute('aria-label') !== plusLabel) $plus.setAttribute('aria-label', plusLabel);
        const n = counts.get(handle) || 0;
        const $count = card.querySelector('.pd-bb-card__count');
        // Only touch the DOM when something changed, so the MutationObserver doesn't loop on our own edits
        const label = `${n} in bundle`;
        if ($count && $count.textContent !== label) $count.textContent = label;
        if ($count && $count.hidden !== !n) $count.hidden = !n;
        if (card.classList.contains('is-in-bundle') !== n > 0) card.classList.toggle('is-in-bundle', n > 0);
      });
    }

    /* ---------- rendering ---------- */

    render() {
      const count = this.items.length;
      const { subtotal, tier, savings, total } = this.totals();
      const next = this.nextTier(count);

      // Slots, with each tier's discount printed under its slot
      const visible = this.slotCount;
      let slotHtml = '';
      for (let n = 1; n <= visible; n++) {
        const item = this.items[n - 1];
        const t = this.tiers.find((x) => x.qty === n);
        const overflow = n === visible && count > visible ? count - visible + 1 : 0;
        slotHtml += `
          <span class="pd-bb__slot${item ? ' is-filled' : ''}${t && count >= t.qty ? ' is-reached' : ''}">
            <span class="pd-bb__slot-box">${item ? `<img src="${escapeHtml(this.imageFor(item, 120))}" alt="">` : ''}${overflow ? `<span class="pd-bb__slot-more">+${overflow}</span>` : ''}</span>
            <span class="pd-bb__slot-tag">${t ? `${t.pct}%` : ''}</span>
          </span>`;
      }
      this.$('[data-slots]').innerHTML = slotHtml;

      // Message and summary. Parts are joined with a spaced dot (the font's "·" sits too tight)
      const dot = '<span class="pd-bb__dot" aria-hidden="true"></span>';
      let msg;
      if (!this.tiers.length) msg = count ? `${count} in your bundle` : 'Click the + to add a pillow to your bundle';
      else if (!tier) {
        const needed = next.qty - count;
        msg = count === 0 ? `Pick ${next.qty} pillows, save ${next.pct}%` : `Add ${needed} more to save ${next.pct}%`;
      } else if (next) msg = `${tier.pct}% off${dot}<span class="pd-bb__msg-next">${next.qty - count} more for ${next.pct}%</span>`;
      else msg = `${tier.pct}% off your bundle`;
      this.$('[data-msg]').innerHTML = msg;
      this.$('[data-sum]').innerHTML = count
        ? `<span class="pd-bb__sum-count">${count} pillow${count === 1 ? '' : 's'}${dot}</span>${savings ? `<s>${this.money(subtotal)}</s> ` : ''}<strong>${this.money(total)}</strong>`
        : 'Click the + to add a pillow to your bundle';
      this.$('[data-edit-label]').textContent = count ? `Edit (${count})` : 'Edit';
      this.classList.toggle('has-items', count > 0);

      // Panel rows with size and fill dropdowns
      this.$('[data-items]').innerHTML = this.items.map((item, index) => this.rowHtml(item, index)).join('');
      this.$('[data-empty]').hidden = count > 0;
      this.$('[data-subtotal]').textContent = this.money(subtotal);
      this.$('[data-savings-row]').hidden = !tier;
      if (tier) {
        this.$('[data-savings-label]').textContent = `Bundle savings (${tier.pct}%)`;
        this.$('[data-savings]').textContent = `−${this.money(savings)}`;
      }
      this.$('[data-total]').textContent = this.money(total);

      // Cart form: group pillows by variant into cart lines
      const lines = new Map();
      this.items.forEach((i) => lines.set(i.variantId, (lines.get(i.variantId) || 0) + 1));
      this.$('[data-form-items]').innerHTML = [...lines]
        .map(([id, qty], i) => `<input type="hidden" name="items[${i}][id]" value="${id}"><input type="hidden" name="items[${i}][quantity]" value="${qty}">`)
        .join('');

      this.decorateCards();
    }

    rowHtml(item, index) {
      const p = this.product(item);
      const v = this.variant(item);
      const { line, name } = this.names(p);
      const selects = p.options
        .map((optionName, oi) => {
          const values = [...new Set(p.variants.map((x) => x.options[oi]))];
          const options = values
            .map((value) => {
              const wanted = v.options.map((o, i) => (i === oi ? value : o));
              const match = p.variants.find((x) => x.options.every((o, i) => o === wanted[i]));
              const soldOut = !match || !match.available;
              return `<option value="${escapeHtml(value)}" ${value === v.options[oi] ? 'selected' : ''}>${escapeHtml(value)}${soldOut ? ' (sold out)' : ''}</option>`;
            })
            .join('');
          return `<label class="pd-bb__select pd-bb__select--sm"><select data-row-option="${oi}" aria-label="${escapeHtml(optionName)}">${options}</select></label>`;
        })
        .join('');
      return `
        <li class="pd-bb__row" data-key="${item.key}">
          <img class="pd-bb__row-img" src="${escapeHtml(this.imageFor(item))}" alt="" width="56" height="56" loading="lazy">
          <div class="pd-bb__row-main">
            <p class="pd-bb__row-name"><span>${index + 1}.</span> ${escapeHtml(name)} <small>${escapeHtml(line)}</small></p>
            <div class="pd-bb__row-opts">${selects}</div>
          </div>
          <div class="pd-bb__row-end">
            <span class="pd-bb__row-price">${this.money(v.price)}</span>
            <button type="button" class="pd-bb__remove" data-remove aria-label="Remove ${escapeHtml(name)}">Remove</button>
          </div>
        </li>`;
    }

    /* ---------- actions ---------- */

    async add(handle, card) {
      card?.classList.add('is-adding');
      try {
        const product = await this.loadProduct(handle);
        const wanted = this.wantedOptions(product);
        const variant = this.pickVariant(product, wanted);
        if (!variant) {
          this.toast(`${this.names(product).name} is sold out`);
          return;
        }
        this.items.push({ key: ++this.uid, handle, variantId: variant.id });
        this.save();
        this.render();
        this.bump();
        // Just say what was added; no apology if the first choice was unavailable
        this.toast(`${this.names(product).name} added: ${variant.options.join(', ')}`);
      } catch {
        this.toast("Couldn't add that pillow. Try again.");
      } finally {
        card?.classList.remove('is-adding');
      }
    }

    changeOption(key, optionIndex, value) {
      const item = this.items.find((i) => i.key === key);
      const p = this.product(item);
      const v = this.variant(item);
      const wanted = Object.fromEntries(p.options.map((name, i) => [name, i === optionIndex ? value : v.options[i]]));
      const next = this.pickVariant(p, wanted);
      if (next) item.variantId = next.id;
      this.save();
      this.render();
    }

    remove(key) {
      this.items = this.items.filter((i) => i.key !== key);
      this.save();
      this.render();
    }

    setPanel(open) {
      this.$('[data-panel]').hidden = !open;
      this.classList.toggle('is-panel-open', open);
      this.$('.pd-bb__edit').setAttribute('aria-expanded', open);
    }

    bump() {
      this.classList.remove('is-bumped');
      void this.offsetWidth;
      this.classList.add('is-bumped');
    }

    toast(text) {
      const $toast = this.$('[data-toast]');
      $toast.textContent = text;
      $toast.hidden = false;
      clearTimeout(this.toastTimer);
      this.toastTimer = setTimeout(() => ($toast.hidden = true), 2400);
    }

    bindEvents() {
      // The + on each product card adds it to the bundle; the rest of the card opens the product page as usual
      document.addEventListener(
        'click',
        (e) => {
          if (!e.target.closest('.pd-bb-card__plus')) return;
          const card = e.target.closest('main product-card');
          if (!card || this.contains(card)) return;
          const handle = card.dataset.pdHandle || handleFromCard(card);
          if (!handle) return;
          e.preventDefault();
          e.stopPropagation();
          this.add(handle, card);
        },
        true
      );

      this.addEventListener('click', (e) => {
        const target = e.target.closest('button');
        if (!target || !this.contains(target)) return;
        if (target.matches('[data-close-panel]')) {
          this.setPanel(false);
        } else if (target.matches('[data-open-panel]')) {
          this.setPanel(this.$('[data-panel]').hidden);
        } else if (target.matches('[data-remove]')) {
          this.remove(Number(target.closest('[data-key]').dataset.key));
        } else if (target.matches('[data-submit-proxy]')) {
          if (!this.items.length) {
            this.toast('Click the + to add a pillow to your bundle');
            return;
          }
          target.classList.add('is-loading');
          this.$('[data-form]').requestSubmit();
        }
      });

      this.addEventListener('change', (e) => {
        if (e.target.matches('[data-row-option]')) {
          this.changeOption(Number(e.target.closest('[data-key]').dataset.key), Number(e.target.dataset.rowOption), e.target.value);
        }
      });

      // product-form dispatches cart:updated on itself when the add finishes
      this.$('[data-form]').addEventListener('cart:updated', (e) => {
        this.$('[data-submit-proxy]').classList.remove('is-loading');
        if (e.detail?.error) {
          this.setPanel(true);
          return;
        }
        this.items = [];
        this.save();
        this.render();
        this.setPanel(false);
      });
    }
  }

  if (!customElements.get('pd-bundle-bar')) customElements.define('pd-bundle-bar', PdBundleBar);
})();
